import router from '@/router'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { getRawRoute } from '@/utils/routerHelper'
import { defineStore } from 'pinia'
import { store } from '../index'
import { findIndex } from '@/utils'
import isEqual from 'lodash-es/isEqual'
import { nameMap } from '@/utils/componentMap'
// import { useUserStoreWithOut } from './user'

const DEFAULT_CACHED_VIEW_LIST = ['Cockpit', 'DataSearchIndex', 'Home']
let tagsViewInstance: any = null

export interface TagsViewState {
  visitedViews: RouteLocationNormalizedLoaded[]
  cachedViews: Set<string>
  selectedTag?: RouteLocationNormalizedLoaded
}

export const useTagsViewStore = defineStore('tagsView', {
  state: (): TagsViewState => ({
    visitedViews: [],
    cachedViews: new Set(DEFAULT_CACHED_VIEW_LIST),
    selectedTag: undefined
  }),
  getters: {
    getVisitedViews(): RouteLocationNormalizedLoaded[] {
      return this.visitedViews
    },
    getCachedViews(): string[] {
      return Array.from(this.cachedViews)
    },
    getSelectedTag(): RouteLocationNormalizedLoaded | undefined {
      return this.selectedTag
    }
  },
  actions: {
    // 新增缓存和tag
    addView(view: RouteLocationNormalizedLoaded): void {
      this.addVisitedView(view)
      this.addCachedView()
    },
    // 新增tag
    addVisitedView(view: RouteLocationNormalizedLoaded) {
      let hasRouter = false
      for (let i = 0; i < this.visitedViews.length; i++) {
        const v = this.visitedViews[i]
        if (v.path === view.path) {
          // 判断v的param和query 与view是否相同,如果相同返回true 如果不同返回false
          if (isEqual(v.query, view?.query) && isEqual(v.params, view?.params)) {
            hasRouter = true
            break // 跳出循环
          }
        }
      }
      if (hasRouter) return
      if (view.meta?.noTagsView) return
      // this.visitedViews 最多20个，超过20个就删除第一个
      if (this.visitedViews.length >= 20) {
        this.visitedViews.shift()
      }
      this.visitedViews.push(
        Object.assign({}, view, {
          title: view.meta?.title || 'no-name',
          key: view.fullPath
        })
      )
    },
    // 新增缓存
    addCachedView() {
      // 默认一直在缓存中的页面 驾驶舱 数据搜索 对应的路由名字
      // const DEFAULT_CACHED_VIEW_LIST = ['Cockpit', 'DataSearchIndex']
      const cacheMap: Set<string> = new Set()
      for (const v of this.visitedViews) {
        const item = getRawRoute(v)
        const needCache = !item?.meta?.noCache
        if (!needCache) {
          continue
        }
        const name = (nameMap[item.name] || item.name) as string
        cacheMap.add(name)
      }
      DEFAULT_CACHED_VIEW_LIST.forEach((name) => {
        cacheMap.add(name)
      })
      if (Array.from(this.cachedViews).sort().toString() === Array.from(cacheMap).sort().toString())
        return
      this.cachedViews = cacheMap
    },
    // 删除某个
    delView(view: RouteLocationNormalizedLoaded) {
      this.delVisitedView(view)
      this.addCachedView()
    },
    // 删除tag
    delVisitedView(view: RouteLocationNormalizedLoaded) {
      for (const [i, v] of this.visitedViews.entries()) {
        if (v.fullPath === view.fullPath) {
          this.visitedViews.splice(i, 1)
          break
        }
      }
    },
    // 删除缓存
    delCachedView(specifiedRouter?: any) {
      const route = specifiedRouter || router.currentRoute.value
      const index = findIndex<string>(this.getCachedViews, (v) => v === route.name)
      if (index > -1) {
        this.cachedViews.delete(this.getCachedViews[index])
      }
    },
    // 删除所有缓存和tag
    delAllViews() {
      this.delAllVisitedViews()
      this.addCachedView()
    },
    // 删除所有tag
    delAllVisitedViews() {
      // const userStore = useUserStoreWithOut()
      // // const affixTags = this.visitedViews.filter((tag) => tag.meta.affix)
      // this.visitedViews = userStore.getUserInfo
      //   ? this.visitedViews.filter((tag) => tag?.meta?.affix)
      //   : []
      this.visitedViews = []
    },
    // 删除其它
    delOthersViews(view: RouteLocationNormalizedLoaded) {
      this.delOthersVisitedViews(view)
      this.addCachedView()
    },
    // 删除其它tag
    delOthersVisitedViews(view: RouteLocationNormalizedLoaded) {
      this.visitedViews = this.visitedViews.filter((v) => {
        return v?.meta?.affix || v.path === view.path
      })
    },
    // 删除左侧
    delLeftViews(view: RouteLocationNormalizedLoaded) {
      const index = findIndex<RouteLocationNormalizedLoaded>(
        this.visitedViews,
        (v) => v.path === view.path
      )
      if (index > -1) {
        this.visitedViews = this.visitedViews.filter((v, i) => {
          return v?.meta?.affix || v.path === view.path || i > index
        })
        this.addCachedView()
      }
    },
    // 删除右侧
    delRightViews(view: RouteLocationNormalizedLoaded) {
      const index = findIndex<RouteLocationNormalizedLoaded>(
        this.visitedViews,
        (v) => v.path === view.path
      )
      if (index > -1) {
        this.visitedViews = this.visitedViews.filter((v, i) => {
          return v?.meta?.affix || v.path === view.path || i < index
        })
        this.addCachedView()
      }
    },
    updateVisitedView(view: RouteLocationNormalizedLoaded) {
      for (let v of this.visitedViews) {
        if (v.path === view.path) {
          v = Object.assign(v, view)
          break
        }
      }
    },
    // 设置当前选中的tag
    setSelectedTag(tag: RouteLocationNormalizedLoaded) {
      this.selectedTag = tag
    },
    setTitle(title: string, path?: string) {
      for (const v of this.visitedViews) {
        if (v.path === (path ?? this.selectedTag?.path)) {
          v.meta.title = title
          break
        }
      }
    }
  },
  persist: false
})

export const useTagsViewStoreWithOut = () => {
  // if (tagsViewInstance){}
  // return useTagsViewStore(store)
  if (!tagsViewInstance) {
    tagsViewInstance = useTagsViewStore(store)
  }
  return tagsViewInstance
}
